import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "../App";
import { Sparkles, Plus, Edit3, Trash2, Eye, Save, X, ImagePlus } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AUDIENCE_LABELS = { worker: "Worker", supervisor: "Supervisor", admin: "Admin" };

const TutorialsAdminPage = () => {
  const { currentCompany } = useAuth();
  const [tutorials, setTutorials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDraft, setShowDraft] = useState(false);
  const [editing, setEditing] = useState(null);

  const fetchList = async () => {
    if (!currentCompany) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/super-admin/companies/${currentCompany.id}/tutorials`);
      setTutorials(res.data || []);
    } catch (e) {
      toast.error("Failed to load tutorials");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchList(); }, [currentCompany?.id]);

  if (!currentCompany) {
    return <div className="text-center py-20 text-slate-400">Open a company first.</div>;
  }

  const onDelete = async (id) => {
    if (!window.confirm("Delete this tutorial?")) return;
    try {
      await axios.delete(`${API}/super-admin/tutorials/${id}`);
      toast.success("Deleted");
      fetchList();
    } catch (e) { toast.error("Failed to delete"); }
  };

  const onPublishToggle = async (t) => {
    const next = t.status === "published" ? "draft" : "published";
    try {
      await axios.put(`${API}/super-admin/tutorials/${t.id}`, { status: next });
      toast.success(next === "published" ? "Published to company" : "Unpublished");
      fetchList();
    } catch (e) { toast.error("Failed"); }
  };

  return (
    <div className="space-y-4" data-testid="tutorials-admin-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold uppercase tracking-tight">Tutorials</h1>
          <p className="text-slate-500 text-sm mt-1">
            {currentCompany.name} ({currentCompany.code}) · workers only see Published tutorials
          </p>
        </div>
        <button
          onClick={() => setShowDraft(true)}
          className="px-4 py-3 bg-orange-500 hover:bg-orange-600 text-white rounded-md font-semibold flex items-center gap-2"
          data-testid="new-tutorial-btn"
        >
          <Sparkles className="w-5 h-5" />
          AI Draft New Tutorial
        </button>
      </div>

      {loading ? (
        <div className="text-slate-400 text-center py-10">Loading…</div>
      ) : tutorials.length === 0 ? (
        <div className="text-slate-400 text-center py-10">No tutorials yet — click "AI Draft New Tutorial" to start.</div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {tutorials.map(t => (
            <div key={t.id} className="bg-white border-2 border-slate-200 rounded-md p-4" data-testid={`tutorial-card-${t.id}`}>
              <div className="flex items-center justify-between mb-2">
                <span className={`text-[10px] uppercase font-bold px-2 py-1 rounded ${t.status === "published" ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-600"}`}>
                  {t.status}
                </span>
                <span className="text-[10px] text-slate-500">{AUDIENCE_LABELS[t.audience] || t.audience}</span>
              </div>
              <h3 className="font-bold text-slate-900 mb-1">{t.title}</h3>
              <p className="text-xs text-slate-500 mb-2 line-clamp-2">{t.summary}</p>
              <div className="text-[10px] text-slate-400 mb-3">
                {t.screenshot_urls?.length || 0} screenshot(s) · ${t.billable_usd?.toFixed(2)} billable
              </div>
              <div className="flex gap-1">
                <button onClick={() => setEditing(t)} className="flex-1 text-xs px-2 py-1 bg-slate-100 hover:bg-slate-200 rounded font-semibold" data-testid={`edit-tutorial-${t.id}`}>
                  <Edit3 className="w-3 h-3 inline mr-1" />Review
                </button>
                <button onClick={() => onPublishToggle(t)} className={`flex-1 text-xs px-2 py-1 rounded font-semibold ${t.status === "published" ? "bg-slate-100 hover:bg-slate-200 text-slate-700" : "bg-green-600 hover:bg-green-700 text-white"}`} data-testid={`publish-tutorial-${t.id}`}>
                  {t.status === "published" ? "Unpublish" : "Publish"}
                </button>
                <button onClick={() => onDelete(t.id)} className="text-xs px-2 py-1 bg-red-100 hover:bg-red-200 text-red-700 rounded">
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showDraft && (
        <DraftModal company={currentCompany} onClose={() => setShowDraft(false)} onCreated={async () => { setShowDraft(false); await fetchList(); }} />
      )}
      {editing && (
        <EditModal tutorial={editing} onClose={() => setEditing(null)} onSaved={async () => { setEditing(null); await fetchList(); }} />
      )}
    </div>
  );
};

const DraftModal = ({ company, onClose, onCreated }) => {
  const [topic, setTopic] = useState("");
  const [audience, setAudience] = useState("worker");
  const [featureKey, setFeatureKey] = useState("");
  const [notes, setNotes] = useState("");
  const [drafting, setDrafting] = useState(false);

  const submit = async () => {
    if (!topic.trim()) return;
    setDrafting(true);
    try {
      await axios.post(`${API}/super-admin/companies/${company.id}/tutorials/draft`, {
        topic: topic.trim(),
        audience,
        feature_key: featureKey || null,
        notes,
      });
      toast.success("AI draft created — review it now");
      await onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Draft failed");
    } finally {
      setDrafting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" data-testid="draft-tutorial-modal">
      <div className="bg-white rounded-md w-full max-w-md shadow-2xl">
        <div className="border-b-2 p-4 flex items-center justify-between">
          <h2 className="text-xl font-bold uppercase">AI Draft Tutorial</h2>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4 space-y-3">
          <div>
            <label className="block text-xs font-semibold uppercase text-slate-500 mb-1">Topic *</label>
            <input value={topic} onChange={e => setTopic(e.target.value)} placeholder="How to clock on to a job"
              className="w-full border-2 border-slate-200 rounded px-3 py-2" data-testid="tutorial-topic" />
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase text-slate-500 mb-1">Audience</label>
            <select value={audience} onChange={e => setAudience(e.target.value)} className="w-full border-2 border-slate-200 rounded px-3 py-2">
              <option value="worker">Worker</option>
              <option value="supervisor">Supervisor</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase text-slate-500 mb-1">Linked feature (optional)</label>
            <select value={featureKey} onChange={e => setFeatureKey(e.target.value)} className="w-full border-2 border-slate-200 rounded px-3 py-2">
              <option value="">— None —</option>
              {(company.enabled_features || []).map(k => <option key={k} value={k}>{k}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase text-slate-500 mb-1">Extra notes for AI</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2}
              placeholder="e.g. Mention the new geofence rule" className="w-full border-2 border-slate-200 rounded px-3 py-2 text-sm" />
          </div>
          <div className="bg-amber-50 border-2 border-amber-200 rounded p-2 text-xs text-amber-700">
            Generation cost ~ $0.60–$1.20 billable. You can review/edit before publishing.
          </div>
        </div>
        <div className="border-t-2 p-4 flex gap-2">
          <button onClick={onClose} className="flex-1 px-4 py-2 border-2 border-slate-200 rounded font-semibold">Cancel</button>
          <button onClick={submit} disabled={drafting || !topic.trim()} className="flex-1 px-4 py-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white rounded font-bold flex items-center justify-center gap-2" data-testid="generate-tutorial-btn">
            <Sparkles className="w-4 h-4" />{drafting ? "Drafting…" : "Generate"}
          </button>
        </div>
      </div>
    </div>
  );
};

const EditModal = ({ tutorial, onClose, onSaved }) => {
  const [title, setTitle] = useState(tutorial.title);
  const [summary, setSummary] = useState(tutorial.summary);
  const [body, setBody] = useState(tutorial.body_markdown);
  const [captions, setCaptions] = useState(tutorial.screenshot_captions || []);
  const [screenshots, setScreenshots] = useState(tutorial.screenshot_urls || []);
  const [saving, setSaving] = useState(false);
  const [comments, setComments] = useState(tutorial.comments || []);
  const [newComment, setNewComment] = useState("");
  const [regenLoading, setRegenLoading] = useState(false);

  const addComment = async () => {
    if (!newComment.trim()) return;
    try {
      const res = await axios.post(`${API}/super-admin/tutorials/${tutorial.id}/comments`, { comment: newComment.trim() });
      setComments(c => [...c, res.data]);
      setNewComment("");
      toast.success("Comment added");
    } catch (e) { toast.error("Failed to add comment"); }
  };

  const regenerate = async () => {
    if (!window.confirm("Regenerate this tutorial using the comments above? This will cost another draft fee.")) return;
    setRegenLoading(true);
    try {
      const res = await axios.post(`${API}/super-admin/tutorials/${tutorial.id}/regenerate`);
      setTitle(res.data.title);
      setSummary(res.data.summary || "");
      setBody(res.data.body_markdown);
      setCaptions(res.data.screenshot_captions || []);
      toast.success("Regenerated — review and publish when ready");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Regenerate failed");
    } finally { setRegenLoading(false); }
  };

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const dataUrls = await Promise.all(files.map(f => new Promise((res) => {
      const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(f);
    })));
    setScreenshots(prev => [...prev, ...dataUrls]);
  };

  const removeShot = (i) => setScreenshots(s => s.filter((_, idx) => idx !== i));

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/super-admin/tutorials/${tutorial.id}`, {
        title, body_markdown: body, screenshot_urls: screenshots, captions,
      });
      toast.success("Saved");
      await onSaved();
    } catch (e) {
      toast.error("Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" data-testid="edit-tutorial-modal">
      <div className="bg-white rounded-md w-full max-w-3xl shadow-2xl max-h-[95vh] flex flex-col">
        <div className="border-b-2 p-4 flex items-center justify-between sticky top-0 bg-white">
          <h2 className="text-xl font-bold uppercase">Review / Edit Tutorial</h2>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          <input value={title} onChange={e => setTitle(e.target.value)} className="w-full border-2 border-slate-200 rounded px-3 py-2 font-bold" data-testid="edit-tutorial-title" />
          <textarea value={summary} onChange={e => setSummary(e.target.value)} rows={2} placeholder="Short summary" className="w-full border-2 border-slate-200 rounded px-3 py-2 text-sm" />
          <textarea value={body} onChange={e => setBody(e.target.value)} rows={16} className="w-full border-2 border-slate-200 rounded px-3 py-2 text-sm font-mono" data-testid="edit-tutorial-body" />
          <div>
            <div className="text-xs font-semibold uppercase mb-2 text-slate-500">Screenshots</div>
            <label className="inline-flex items-center gap-2 px-3 py-2 bg-slate-100 hover:bg-slate-200 rounded cursor-pointer text-sm font-semibold">
              <ImagePlus className="w-4 h-4" /> Add screenshots
              <input type="file" accept="image/*" multiple onChange={handleUpload} className="hidden" data-testid="upload-screenshots" />
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-2">
              {screenshots.map((url, i) => (
                <div key={i} className="relative border-2 border-slate-200 rounded overflow-hidden">
                  <img src={url} alt={`Screenshot ${i + 1}`} className="w-full h-32 object-cover" />
                  <input value={captions[i] || ""} onChange={e => { const next = [...captions]; next[i] = e.target.value; setCaptions(next); }}
                    placeholder={`Caption ${i + 1}`} className="absolute bottom-0 inset-x-0 bg-black/70 text-white text-xs px-2 py-1" />
                  <button onClick={() => removeShot(i)} className="absolute top-1 right-1 bg-red-600 text-white rounded p-1"><X className="w-3 h-3" /></button>
                </div>
              ))}
            </div>
          </div>

          {/* ===== Approval / Comments thread ===== */}
          <div className="border-t-2 border-slate-200 pt-4">
            <div className="text-xs font-semibold uppercase mb-2 text-slate-500">Approval & Fix Comments</div>
            <p className="text-xs text-slate-500 mb-2">Found something wrong? Drop comments here, then click Regenerate to redraft with your feedback.</p>
            {comments.length > 0 ? (
              <div className="space-y-2 mb-3">
                {comments.map(c => (
                  <div key={c.id} className="bg-amber-50 border border-amber-200 rounded p-2 text-xs">
                    <div className="text-slate-700">{c.comment}</div>
                    <div className="text-[10px] text-slate-400 mt-1">{c.by_email} · {new Date(c.at).toLocaleString()}</div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-xs text-slate-400 mb-3">No comments yet.</div>
            )}
            <div className="flex gap-2 mb-2">
              <input value={newComment} onChange={e => setNewComment(e.target.value)} placeholder="e.g. Step 3 references the wrong button name…"
                className="flex-1 border-2 border-slate-200 rounded px-2 py-1 text-sm" data-testid="tutorial-comment-input" />
              <button onClick={addComment} className="px-3 py-1 bg-slate-900 hover:bg-slate-800 text-white rounded text-sm font-semibold" data-testid="add-comment-btn">Add</button>
            </div>
            <button
              onClick={regenerate}
              disabled={regenLoading || comments.length === 0}
              className="w-full px-3 py-2 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white rounded font-semibold text-sm flex items-center justify-center gap-2"
              data-testid="regenerate-tutorial-btn"
              title={comments.length === 0 ? "Add at least one comment first" : ""}
            >
              {regenLoading ? "Regenerating…" : "🔁 Regenerate with feedback"}
            </button>
          </div>
        </div>
        <div className="border-t-2 p-4 flex gap-2 sticky bottom-0 bg-white">
          <button onClick={onClose} className="flex-1 px-4 py-2 border-2 border-slate-200 rounded font-semibold">Cancel</button>
          <button onClick={save} disabled={saving} className="flex-1 px-4 py-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white rounded font-bold flex items-center justify-center gap-2" data-testid="save-tutorial-btn">
            <Save className="w-4 h-4" />{saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TutorialsAdminPage;
